import React from 'react'
import PropTypes from 'prop-types'
import { ProvidedMatchAttach } from '@fs/zion-record-details'
import { Card, H2, Row, Separator } from '@fs/zion-ui'
import { LogoFamilysearch } from '@fs/zion-icon'
import { useTranslation } from 'react-i18next'
import { REVIEWED_COLOR } from '../shared/utils/panelConstants'

/**
 * TreeAttachmentCard - A card component that displays the Family Tree attachment interface for a record.
 *
 * This component provides a consistent card layout with a FamilySearch logo header and the
 * ProvidedMatchAttach component for managing tree attachments. It allows users to see and manage
 * the connection between indexed records and Family Tree profiles.
 *
 * @param {Object} props - Component props
 * @param {string} props.ark - The ARK (Archival Resource Key) identifier for the record/person.
 *                              Format: '/ark:/61903/{id}' or just the ID portion.
 *
 * @returns {React.ReactElement} The rendered TreeAttachmentCard component
 *
 * @example
 * <TreeAttachmentCard ark="/ark:/61903/1:1:ABC123" />
 *
 * @example
 * // With just the ID
 * <TreeAttachmentCard ark="1:1:ABC123" />
 */
const TreeAttachmentCard = ({ ark }) => {
  const [t] = useTranslation()
  return (
    <Card margins="xxs" edges="border">
      <Row gutters="xxs" alignItemsY="middle">
        <LogoFamilysearch size="sm" background={REVIEWED_COLOR} />
        <H2 growX size="H5">
          {t('match-attach-card.title', 'Tree Attachment')}
        </H2>
      </Row>
      <Separator size="xs" />
      <ProvidedMatchAttach ark={ark} />
    </Card>
  )
}

TreeAttachmentCard.propTypes = {
  /** The ARK identifier for the record/person (e.g., '/ark:/61903/1:1:ABC123' or '1:1:ABC123') */
  ark: PropTypes.string.isRequired,
}

export default TreeAttachmentCard
